import classnames from 'classnames2';
import Cleave from 'cleave.js/react.js';
import { capitalize } from 'lodash-es';
import { DateTime } from 'luxon';
import React from 'react';

import { IBaseProps } from '../../utils';

import { Error } from '../../Error';
import { Icon } from '../../Icon';

import { getContainerClasses } from '../BaseInput/BaseInput';
import { InputSizes, ValidationStates } from '../enums';

import styles from '../BaseInput/BaseInput.module.scss';

import { IDateInputOption } from './types';

export interface IDateInputProps extends IBaseProps {
  /** Id attribute assigned to the input field.
   * Used to associate the input with other form elements (Ex: label)
   */
  id?: string;
  size?: 'default' | 'small' | 'large';
  /** The current value of the input that is displayed to the user */
  value?: string;
  /** Validation state of the input which can be either success or error */
  validationState?: ValidationStates;
  /** Set to true if you want to disable the input from accepting changes from the user */
  disabled?: boolean;
  /** Determines if field is required */
  required?: boolean;
  /** Application level error message that the component will display regardless of internal error state */
  errorMessage?: string;
  /** Whether or not to display the error message when the date is invalid */
  showErrorMessage?: boolean;
  allowPartialDate?: boolean;
  /** Optional prop for placeholder, if not set placeholder for date input will be MM / DD / YYYY */
  placeholder?: string;
  /** Optional prop customized date format option. The date boolean value should always be true if this is used
   *  If not specified, the date input will format date with datePattern ['m','d','Y] and delimiter ' / '
   */
  formatOption?: IDateInputOption;
  /** This callback is executed anytime validation happens in the date input component.
   * Returns true or false for isDateValid,
   * the current date being validated, and whether or not the date is complete
   */
  onDateValid?(isDateValid: boolean, date: string, isCompleteDate: boolean, msg: string): void;
  /** Application level error message that the component will display regardless of internal error state */
  onChange?(newValue: string): void;
}

const handleErrorMsgFormat = (str, repeatTimes) => {
  let errorMsgDateFormat = '';
  for (let i = 0; i < repeatTimes; i++) {
    errorMsgDateFormat += str;
  }
  return errorMsgDateFormat;
};
export function createDateInput(sizeType: InputSizes) {
  return class DateInputBase extends React.Component<IDateInputProps, any> {
    static defaultProps = {
      onChange: () => {},
      onDateValid: () => {}
    };

    static displayName = `Input.Date.${sizeType}`;

    state = {
      validationState: ValidationStates.None,
      errorMessage: null
    };

    handleOnBlur = (event) => {
      let dateFormat = 'MM / DD / YYYY';

      if (this.props.formatOption) {
        const dateFormatPositions = ['', '', ''];
        const delimiter = this.props.formatOption.delimiter;
        this.props.formatOption.datePattern.forEach((option, index) => {
          const digit = option === 'Y' ? 4 : 2;

          dateFormatPositions[index] = `${handleErrorMsgFormat(this.props.formatOption.datePattern[index], digit)}`;
          if (index !== dateFormatPositions.length - 1) {
            dateFormatPositions[index] = `${dateFormatPositions[index]}${delimiter}`;
          }
        });

        dateFormat = `${dateFormatPositions.join('')}`;
      }

      const validDateState = {
        validationState: ValidationStates.Success,
        errorMessage: ''
      };
      const invalidDateState = {
        validationState: ValidationStates.Error,
        errorMessage: `The date specified is invalid. Enter a date as ${dateFormat}.`
      };
      const requiredDateState = {
        validationState: ValidationStates.Error,
        errorMessage: `A date is required. Enter a date as ${dateFormat}.`
      };
      const value = event.target.value;

      // Luxon requires the format without spaces and in lowercase
      const parseFormat = dateFormat.replace(/\s/g, '').toLowerCase();
      const date = DateTime.fromFormat(
        value
          .split(this.props.formatOption?.delimiter ?? ' / ')
          .join(this.props.formatOption?.delimiter.replace(/\s/g, '') ?? '/'),
        parseFormat
      );

      let state = null;
      if (!value && this.props.required) {
        state = requiredDateState;
        this.props.onDateValid(false, value, false, state.errorMessage);
      } else if (date.isValid) {
        state = validDateState;
        this.props.onDateValid(true, value, true, state.errorMessage);
      } else {
        state = invalidDateState;
        this.props.onDateValid(false, value, false, state.errorMessage);
      }
      this.setState(state);
    };

    handleOnChange = (event) => {
      const value = event.target.value;
      this.props.onChange(value.split(' / ').join('/'));
    };

    render() {
      const inputSize = capitalize(this.props.size) || sizeType;
      const validationState = this.props.validationState || this.state.validationState;
      const { showErrorMessage = true } = this.props;

      const containerClasses = getContainerClasses(inputSize, validationState, this.props.disabled);
      const placeholderText = this.props.placeholder ?? 'MM / DD / YYYY';
      const option = this.props.formatOption ?? { date: true, datePattern: ['m', 'd', 'Y'], delimiter: ' / ' };
      return (
        <>
          <div className={classnames(this.props.className, containerClasses)}>
            <Cleave
              id={this.props.id}
              className={styles.input}
              disabled={this.props.disabled}
              options={option}
              value={this.props.value}
              placeholder={placeholderText}
              onBlur={this.handleOnBlur}
              onChange={this.handleOnChange}
              {...(this.props.dataTest && { 'data-test': this.props.dataTest })}
            />
            {validationState && (
              <span className={styles.iconWrapper}>
                {validationState === ValidationStates.Success && <Icon className={styles.icon} icon="status-success" />}
                {validationState === ValidationStates.Error && <Icon className={styles.icon} icon="status-error" />}
              </span>
            )}
          </div>
          {showErrorMessage && this.state.errorMessage && <Error>{this.state.errorMessage}</Error>}
        </>
      );
    }
  };
}

function generateDateInputWithSizes() {
  const DefaultDateInput = createDateInput(InputSizes.Default);
  return Object.assign(
    DefaultDateInput,
    ...Object.keys(InputSizes).map((sizeType) => ({
      [sizeType]: createDateInput(InputSizes[sizeType])
    }))
  );
}

export const DateInput = generateDateInputWithSizes();
